/**
 * 分享服务
 * 提供文档分享功能的底层服务
 */

import { ShareData, ShareInfo, ShareOptions } from '../types/share'
export type { ShareOptions }

const STORAGE_KEY = 'markflow-shared-docs'
const DEFAULT_EXPIRE_HOURS = 24

/**
 * 将对象编码为 URL 安全的 Base64 字符串
 */
const encodeData = (data: any): string => {
  const json = JSON.stringify(data)
  // 使用 encodeURIComponent 处理非 ASCII 字符，然后转换为 Base64
  const utf8Bytes = encodeURIComponent(json).replace(/%([0-9A-F]{2})/g, (_, p1) => {
    return String.fromCharCode(parseInt(p1, 16))
  })
  return btoa(utf8Bytes)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '')
}

/**
 * 将 URL 安全的 Base64 字符串解码为对象
 */
const decodeData = (base64: string): any => {
  try {
    // 还原标准 Base64 格式
    let str = base64.replace(/-/g, '+').replace(/_/g, '/')
    while (str.length % 4) str += '='
    
    const utf8Bytes = atob(str)
    const json = decodeURIComponent(utf8Bytes.split('').map(c => {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
    }).join(''))
    return JSON.parse(json)
  } catch (e) {
    console.error('Failed to decode share data:', e)
    return null
  }
}

/**
 * 获取所有分享的文档
 */
const getSharedDocuments = (): Record<string, ShareData> => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    return stored ? JSON.parse(stored) : {}
  } catch {
    return {}
  }
}

/**
 * 保存分享的文档
 */
const saveSharedDocuments = (sharedDocs: Record<string, ShareData>): void => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(sharedDocs))
}

/**
 * 分享文档
 */
export const shareDocument = async (
  document: { id: number; title: string; content: string },
  options: ShareOptions = {}
): Promise<ShareInfo> => {
  // 计算过期时间
  const expiresHours = options.expiresHours || DEFAULT_EXPIRE_HOURS
  const expiresAt = Date.now() + expiresHours * 60 * 60 * 1000

  const shareData: ShareData = {
    id: document.id,
    title: document.title,
    content: document.content,
    sharedAt: Date.now(),
    expiresAt
  }

  let shareId: string;

  try {
    // 尝试调用 Cloudflare Function 保存到 KV
    const response = await fetch('/api/share', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(shareData)
    });

    if (response.ok) {
      const result = await response.json() as { id: string };
      shareId = result.id;
    } else {
      throw new Error('API failed');
    }
  } catch (err) {
    console.warn('Cloudflare KV not available, falling back to URL encoding');
    // 兜底方案：使用编码后的数据作为 shareId
    shareId = encodeData(shareData);
  }

  // 为了向后兼容和本地管理，仍然保存到 localStorage
  const sharedDocs = getSharedDocuments()
  sharedDocs[shareId] = shareData
  saveSharedDocuments(sharedDocs)

  // 构建分享URL
  const shareUrl = `${window.location.origin}/share/${shareId}`

  return {
    shareId,
    document: shareData,
    shareUrl
  }
}

/**
 * 获取分享的文档
 */
export const getSharedDocument = async (shareId: string): Promise<ShareData | null> => {
  // 1. 首先尝试从 localStorage 获取（可能是在本机打开）
  const sharedDocs = getSharedDocuments()
  let shareData = sharedDocs[shareId]

  if (shareData) {
    return shareData;
  }

  // 2. 如果本地没有，根据 shareId 长度判断来源
  // 短 ID (通常为 8 位) 来自服务器 KV
  if (shareId.length < 50) {
    try {
      const response = await fetch(`/api/share?id=${shareId}`);
      if (response.ok) {
        return await response.json() as ShareData;
      }
    } catch (err) {
      console.error('Failed to fetch from Cloudflare KV:', err);
    }
  }

  // 3. 尝试从 shareId 直接解码数据（跨设备访问的兜底逻辑）
  return decodeData(shareId) || null
}

/**
 * 删除分享的文档
 */
export const deleteSharedDocument = (shareId: string): boolean => {
  const sharedDocs = getSharedDocuments()

  if (!sharedDocs[shareId]) {
    return false
  }

  delete sharedDocs[shareId]
  saveSharedDocuments(sharedDocs)
  return true
}

/**
 * 清理所有过期的分享
 */
export const cleanupExpiredShares = (): number => {
  const sharedDocs = getSharedDocuments()
  let cleanedCount = 0

  Object.keys(sharedDocs).forEach(shareId => {
    if (sharedDocs[shareId].expiresAt < Date.now()) {
      delete sharedDocs[shareId]
      cleanedCount++
    }
  })

  if (cleanedCount > 0) {
    saveSharedDocuments(sharedDocs)
  }

  return cleanedCount
}

/**
 * 获取分享统计信息
 */
export const getShareStats = async (shareId: string): Promise<{ isValid: boolean; expiresAt: number; isExpired: boolean }> => {
  const shareData = await getSharedDocument(shareId)

  if (!shareData) {
    return {
      isValid: false,
      expiresAt: 0,
      isExpired: true
    }
  }

  const now = Date.now()

  return {
    isValid: true,
    expiresAt: shareData.expiresAt,
    isExpired: shareData.expiresAt < now
  }
}

/**
 * 检查分享是否有效
 */
export const isShareValid = async (shareId: string): Promise<boolean> => {
  return (await getSharedDocument(shareId)) !== null
}

/**
 * 获取所有有效的分享ID
 */
export const getAllValidShares = (): string[] => {
  const sharedDocs = getSharedDocuments()
  const now = Date.now()

  return Object.keys(sharedDocs).filter(shareId =>
    sharedDocs[shareId].expiresAt > now
  )
}